import { ExternalLink } from '@/components/ExternalLink/ExternalLink'
import {
  Box,
  Card,
  HStack,
  Separator,
  Table,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export type DataItem = {
  key: string
  value: ReactNode
  link?: {
    to: string
    params: Record<string, string>
    tooltip?: string
  }
  external?: boolean
}

export type SidebarSection = {
  title: string
  data: DataItem[]
}

export type ClusterInfoSidebarProps = {
  sections: SidebarSection[]
  onClose: () => void
}

const linkStyle = {
  whiteSpace: 'nowrap',
  color: 'var(--chakra-colors-blue-500)',
  fontWeight: 500,
  textDecoration: 'underline',
  alignItems: 'center',
}

export const ClusterInfoSidebar = ({
  sections,
  onClose,
}: ClusterInfoSidebarProps) => {
  const { t } = useTranslation(undefined)
  const noData = t('general.notAvailable')

  return (
    <VStack
      w="100%"
      position="sticky"
      top="70px"
      overflow="auto"
      gap={0}
      style={{
        height: 'calc(var(--app-height) - 120px)',
      }}
    >
      <HStack
        justifyContent="space-between"
        px={2}
        py={2}
        bg="gray.100"
        width="100%"
        position="sticky"
        top="0"
        zIndex="1"
      >
        <Text fontWeight="bold" color="green.800">
          Overview
        </Text>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--chakra-colors-blue-500)',
          }}
        >
          <ChevronLeft size={16} />
        </button>
      </HStack>

      <Card.Root width="100%" flex="1" overflowY="auto">
        {sections.map((section, sectionIndex) => (
          <Box key={`section-${section.title || sectionIndex}`}>
            {sectionIndex > 0 && <Separator my={4} />}
            {section.title && (
              <Card.Header
                fontWeight="700"
                my={0}
                py={2}
                px={6}
                borderBottom="1px solid"
                borderColor="gray.200"
                color="green.800"
              >
                {section.title}
              </Card.Header>
            )}
            <Table.Root>
              <Table.Body>
                {section.data.map((item, itemIndex) => (
                  <Table.Row
                    key={`${section.title}-item-${itemIndex}`}
                    css={{
                      '& > td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <Table.Cell
                      color="text.text-subtle"
                      fontWeight="normal"
                      width="40%"
                      fontSize="sm"
                      whiteSpace="nowrap"
                      py={1}
                    >
                      {item.key}
                    </Table.Cell>
                    <Table.Cell
                      textAlign="right"
                      fontSize="sm"
                      whiteSpace="nowrap"
                      py={1}
                      pr={3}
                      position="relative"
                      css={{
                        '& .data-tag-text': {
                          maxWidth: '180px',
                        },
                      }}
                    >
                      {(() => {
                        if (item.link) {
                          return (
                            <Tooltip.Root>
                              <Tooltip.Trigger>
                                <Link
                                  to={item.link.to}
                                  params={item.link.params}
                                >
                                  {typeof item.value === 'string' ||
                                  item.value === null ||
                                  item.value === undefined ? (
                                    <Text style={linkStyle}>
                                      {item.value || noData}
                                    </Text>
                                  ) : (
                                    <Box style={linkStyle}>{item.value}</Box>
                                  )}
                                </Link>
                              </Tooltip.Trigger>
                              <Tooltip.Positioner>
                                <Tooltip.Content>
                                  {item.link.tooltip || `Go to ${item.key}`}
                                </Tooltip.Content>
                              </Tooltip.Positioner>
                            </Tooltip.Root>
                          )
                        }

                        if (item.external) {
                          return (
                            <ExternalLink
                              href={`mailto:${item.value || noData}`}
                            >
                              {item.value || noData}
                            </ExternalLink>
                          )
                        }

                        if (
                          typeof item.value === 'string' ||
                          item.value === null ||
                          item.value === undefined
                        ) {
                          return item.value || noData
                        }

                        return item.value
                      })()}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        ))}
      </Card.Root>
    </VStack>
  )
}

export default ClusterInfoSidebar
